import { useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import {
  AlertTriangle,
  Ban,
  Banknote,
  CheckCircle2,
  Clock,
  CreditCard,
  ExternalLink,
  Loader2,
  Mail,
  Shield,
  Video,
  Zap,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { ConsultationPaymentsList } from "@/components/account/provider/consultation-payments-list";
import { Button, buttonVariants } from "@/components/ui/button";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { Switch } from "@/components/ui/switch";
import { useApiErrorMessage } from "@/lib/api-error-message";
import { isVaultUnlockCancelled, useProtectedAction } from "@/lib/hooks/use-protected-action";
import { showToast } from "@/lib/ui/toast";

const SUPPORT_EMAIL = "support@pontimed.app";

interface ConnectData {
  status: "none" | "onboarding" | "active" | "restricted" | "disabled";
  payoutsEnabled: boolean;
  onboardedAt: string | null;
  planTier: string;
  hasConnectAccount: boolean;
  commission: {
    basePct: number;
    baseCap: number;
    effectivePct: number;
    effectiveCap: number;
    videoCommissionMultiplier: number;
    pppMultiplier: number;
  };
  stripeDetails: {
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    requirements: string[];
    bankLast4: string | null;
    bankCurrency: string | null;
    defaultCurrency: string | null;
    instantPayoutsAvailable: boolean;
    payoutSchedule: { interval: string; delayDays: number } | null;
  } | null;
}

export const Route = createFileRoute("/_authenticated/account/provider/payments")({
  component: ProviderPaymentsPage,
});

function ProviderPaymentsPage() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Payments</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Stripe Connect onboarding, bank details, and payout schedule.</Trans>
        </p>
      </header>

      <ProviderPaymentsPanel />

      <ConsultationPaymentsList />

      <AccountSecurityBanner />
    </div>
  );
}

/**
 * Stripe Connect payments panel — page content without the page header or
 * security banner, mirroring legacy `PaymentsClient` (embedded by both the
 * standalone payments page above and the provider settings hub's Payments
 * tab in settings.tsx).
 */
export function ProviderPaymentsPanel() {
  const { t } = useLingui();
  const errorMessage = useApiErrorMessage();
  const [data, setData] = useState<ConnectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // The onboarding and Express-dashboard links open the Stripe pages where the
  // payout bank account is set or changed: the API answers vault_locked while
  // a PIN guarding the wallet is not unlocked — the PIN gate opens and the
  // request runs once more after the unlock. Dismissing it does nothing.
  const { runProtected, PinGateElement } = useProtectedAction("wallet");

  const fetchData = async () => {
    try {
      const res = await apiFetch.get<ConnectData>("/api/account/provider/stripe-connect");
      setData(res);
      setErrored(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        const body = err.body as { error?: string } | null;
        if (body?.error === "TOTP_CHALLENGE_REQUIRED") setErrored(true);
        else setErrored(true);
      } else setErrored(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const startOnboarding = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      // Back to Settings → Payments, where the panel and the consultation
      // payments list now live together (the standalone page stays as a deep
      // link). The API accepts any path on our own origins.
      const returnUrl = `${window.location.origin}/account/provider/settings?tab=payments`;
      const res = await runProtected(() =>
        apiFetch.post<{ url: string }>("/api/account/provider/stripe-connect", {
          returnUrl,
        }),
      );
      if (res.url) window.location.href = res.url;
    } catch (err) {
      if (!isVaultUnlockCancelled(err)) setActionError(t`Could not start onboarding`);
    } finally {
      setActionLoading(false);
    }
  };

  const openDashboard = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await runProtected(() =>
        apiFetch.post<{ url: string }>("/api/account/provider/stripe-connect/dashboard", {}),
      );
      // After a PIN prompt the popup is no longer inside the click and may be
      // blocked: open it in this tab instead.
      if (res.url && !window.open(res.url, "_blank")) window.location.href = res.url;
    } catch (err) {
      if (!isVaultUnlockCancelled(err)) setActionError(t`Could not open Stripe dashboard`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleInstantPayouts = async (enabled: boolean) => {
    setActionLoading(true);
    setActionError(null);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.patch("/api/account/provider/stripe-connect", {
        instantPayouts: enabled,
      });
      await fetchData();
      toast.update(t`Saved`, "success");
    } catch (err) {
      toast.update(errorMessage(err, t`Could not update payout schedule`), "error");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading)
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-muted h-24 animate-pulse rounded-xl" />
        ))}
      </div>
    );

  if (errored || !data)
    return (
      <LoadErrorState
        title={<Trans>Could not load payments setup.</Trans>}
        onRetry={() => void fetchData()}
      />
    );

  const scheduleDelay = data.stripeDetails?.payoutSchedule?.delayDays ?? 2;
  const isInstantEnabled =
    !!data.stripeDetails?.instantPayoutsAvailable &&
    data.stripeDetails?.payoutSchedule?.interval === "daily" &&
    scheduleDelay < 2;

  return (
    <div className="space-y-6">
      {actionError && (
        <div className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-xs">
          {actionError}
        </div>
      )}

      <AnimatePresence mode="wait">
        <m.div
          key={data.status}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-5"
        >
          {data.status === "none" && (
            <div className="border-border bg-card rounded-xl border p-6">
              <div className="flex flex-col items-center text-center sm:flex-row sm:gap-5 sm:text-left">
                <div className="bg-primary-soft mb-4 flex size-16 shrink-0 items-center justify-center rounded-2xl sm:mb-0">
                  <Video className="text-primary size-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-foreground text-lg font-semibold">
                    <Trans>Activate online payments</Trans>
                  </h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    <Trans>
                      Connect a Stripe account to accept video consultations + online card payments
                      with automatic payouts.
                    </Trans>
                  </p>
                  <div className="mt-4 flex flex-col gap-2 text-xs sm:flex-row sm:gap-4">
                    <span className="text-muted-foreground inline-flex items-center gap-1.5">
                      <Shield className="text-primary size-3.5" />
                      <Trans>Secure & PCI-compliant</Trans>
                    </span>
                    <span className="text-muted-foreground inline-flex items-center gap-1.5">
                      <CreditCard className="text-primary size-3.5" />
                      <Trans>Cards, Apple Pay, Google Pay</Trans>
                    </span>
                    <span className="text-muted-foreground inline-flex items-center gap-1.5">
                      <Clock className="text-primary size-3.5" />
                      <Trans>Daily or instant payouts</Trans>
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={startOnboarding}
                disabled={actionLoading}
                className="bg-primary text-primary-foreground mt-5 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 sm:w-auto"
              >
                {actionLoading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Banknote className="size-4" />
                )}
                <Trans>Connect Stripe</Trans>
              </button>
            </div>
          )}

          {data.status === "onboarding" && (
            <div className="border-border bg-card rounded-xl border p-6">
              <div className="flex items-start gap-4">
                <div className="bg-warning-soft flex size-12 shrink-0 items-center justify-center rounded-xl">
                  <Clock className="text-warning-strong size-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-foreground text-base font-semibold">
                    <Trans>Onboarding in progress</Trans>
                  </h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    <Trans>Resume Stripe verification to activate payouts.</Trans>
                  </p>
                  <Button
                    variant="outline"
                    onClick={startOnboarding}
                    disabled={actionLoading}
                    className="mt-4 h-auto min-h-11 gap-2 px-4"
                  >
                    {actionLoading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <ExternalLink className="size-4" />
                    )}
                    <Trans>Resume setup</Trans>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {data.status === "active" && (
            <div className="border-border bg-card rounded-xl border p-6">
              <div className="flex items-start gap-4">
                <div className="bg-success-soft flex size-12 shrink-0 items-center justify-center rounded-xl">
                  <CheckCircle2 className="text-success-strong size-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-foreground text-base font-semibold">
                      <Trans>Payments active</Trans>
                    </h3>
                    <span className="bg-success-soft text-success-strong rounded-full px-2.5 py-0.5 text-xs font-medium">
                      <Trans>Active</Trans>
                    </span>
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">
                    <Trans>Your Stripe account is connected and accepting payments.</Trans>
                  </p>

                  {data.stripeDetails?.bankLast4 && (
                    <div className="border-border bg-muted/50 mt-4 flex items-center gap-3 rounded-lg border p-3">
                      <CreditCard className="text-muted-foreground size-5" />
                      <div>
                        <p className="text-foreground text-sm font-medium">
                          •••• {data.stripeDetails.bankLast4}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {data.stripeDetails.bankCurrency?.toUpperCase()}
                          {data.stripeDetails.payoutsEnabled ? (
                            <Trans> — payouts active</Trans>
                          ) : (
                            <Trans> — payouts paused</Trans>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={openDashboard}
                      disabled={actionLoading}
                      className="h-auto min-h-11 gap-2 px-4"
                    >
                      {actionLoading ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <ExternalLink className="size-4" />
                      )}
                      <Trans>Manage on Stripe</Trans>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {data.status === "restricted" && (
            <div className="border-destructive-border bg-destructive-soft rounded-xl border p-6">
              <div className="flex items-start gap-4">
                <div className="bg-background/60 flex size-12 shrink-0 items-center justify-center rounded-xl">
                  <AlertTriangle className="text-destructive-strong size-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-foreground text-base font-semibold">
                    <Trans>Account restricted</Trans>
                  </h3>
                  <p className="text-foreground/80 mt-1 text-sm">
                    <Trans>
                      Stripe needs additional information before you can receive payouts.
                    </Trans>
                  </p>
                  {data.stripeDetails?.requirements &&
                    data.stripeDetails.requirements.length > 0 && (
                      <ul className="text-foreground/80 mt-2 list-inside list-disc text-xs">
                        {/* Stripe repeats one need under several keys
                            (individual.verification.document + …additional_document):
                            dedupe on the mapped kind. */}
                        {[...new Set(data.stripeDetails.requirements.map(stripeRequirementKind))]
                          .slice(0, 3)
                          .map((kind) => (
                            <li key={kind}>
                              <StripeRequirementLabel kind={kind} />
                            </li>
                          ))}
                      </ul>
                    )}
                  <Button
                    onClick={startOnboarding}
                    disabled={actionLoading}
                    className="mt-4 h-auto min-h-11 gap-2 px-4"
                  >
                    {actionLoading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <ExternalLink className="size-4" />
                    )}
                    <Trans>Resolve issues</Trans>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Stripe rejected the account (requirements.disabled_reason
              "rejected.*"): no form PontiMed can reopen fixes it, so say what
              happened and route the doctor to support. */}
          {data.status === "disabled" && (
            <div className="border-destructive-border bg-destructive-soft rounded-xl border p-6">
              <div className="flex items-start gap-4">
                <div className="bg-background/60 flex size-12 shrink-0 items-center justify-center rounded-xl">
                  <Ban className="text-destructive-strong size-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-foreground text-base font-semibold">
                    <Trans>Online payments are turned off</Trans>
                  </h3>
                  <p className="text-foreground/80 mt-1 text-sm">
                    <Trans>
                      Stripe has turned off payments for this account, so patients can't pay you
                      online and no payouts are sent. Write to us and we'll help you sort it out
                      with Stripe.
                    </Trans>
                  </p>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className={cn(
                      buttonVariants({ variant: "outline" }),
                      "mt-4 h-auto min-h-11 gap-2 px-4",
                    )}
                  >
                    <Mail className="size-4" />
                    <Trans>Contact support</Trans>
                  </a>
                </div>
              </div>
            </div>
          )}

          {data.status === "active" && (
            <div className="border-border bg-card rounded-xl border p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "rounded-full p-2",
                      isInstantEnabled ? "bg-warning-soft" : "bg-muted",
                    )}
                  >
                    <Zap
                      className={cn(
                        "size-5",
                        isInstantEnabled ? "text-warning-strong" : "text-muted-foreground",
                      )}
                    />
                  </div>
                  <div>
                    <p className="text-foreground text-sm font-semibold">
                      <Trans>Instant payouts</Trans>
                    </p>
                    <p className="text-muted-foreground text-xs">
                      <Trans>Receive funds within minutes for a 1% fee.</Trans>
                    </p>
                  </div>
                </div>
                {data.stripeDetails?.instantPayoutsAvailable ? (
                  <Switch
                    checked={isInstantEnabled}
                    onCheckedChange={toggleInstantPayouts}
                    disabled={actionLoading}
                  />
                ) : (
                  <span className="bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 text-xs font-medium">
                    <Trans>Unavailable</Trans>
                  </span>
                )}
              </div>

              {/* Fee explanation */}
              <div
                className={cn(
                  "mt-3 rounded-lg p-3 text-xs",
                  isInstantEnabled
                    ? "bg-warning-soft text-warning-strong"
                    : "bg-muted/50 text-muted-foreground",
                )}
              >
                <div className="flex items-start gap-2">
                  <CreditCard className="mt-0.5 size-3.5 shrink-0" />
                  <div>
                    <p className="font-medium">
                      <Trans>1% instant payout fee</Trans>
                    </p>
                    <p className="mt-0.5">
                      <Trans>
                        Stripe charges an additional 1% fee on each instant payout. This fee is
                        deducted from the payout amount and is separate from the platform
                        commission.
                      </Trans>
                    </p>
                  </div>
                </div>
              </div>

              {data.stripeDetails?.payoutSchedule && (
                <div className="text-muted-foreground mt-3 flex items-center gap-2 text-xs">
                  <Clock className="size-3.5" />
                  <span>
                    {isInstantEnabled ? (
                      <Trans>Daily payouts, available within minutes</Trans>
                    ) : (
                      <Trans>
                        Standard payouts arrive in{" "}
                        {data.stripeDetails.payoutSchedule.delayDays || 2} days
                      </Trans>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {data.status !== "none" && (
            <div className="border-border bg-card rounded-xl border p-5">
              <h4 className="text-foreground mb-3 inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase">
                <Banknote className="size-3.5" />
                <Trans>Pontimed commission</Trans>
              </h4>
              <p className="text-muted-foreground text-sm">
                <Trans>
                  Pontimed takes a percentage on each video consultation processed through Stripe.
                </Trans>
              </p>
              {/* No cap since 2026-10-17 (plan D-C18): the tile shows only if an admin sets one. */}
              <div
                className={cn(
                  "mt-3 grid gap-3",
                  data.commission.effectiveCap > 0 ? "grid-cols-2" : "grid-cols-1",
                )}
              >
                <div
                  className={cn(
                    "rounded-lg p-3",
                    data.planTier === "pro"
                      ? "bg-primary-soft"
                      : data.planTier === "expert"
                        ? "bg-accent-soft"
                        : "bg-muted/50",
                  )}
                >
                  <p className="text-muted-foreground text-xs">
                    <Trans>Commission rate</Trans>
                  </p>
                  <p className="text-foreground text-lg font-bold">
                    {data.commission.effectivePct.toFixed(
                      data.commission.effectivePct % 1 === 0 ? 0 : 1,
                    )}
                    %
                  </p>
                  {data.commission.videoCommissionMultiplier !== 1 && (
                    <p className="text-muted-foreground text-[0.625rem]">
                      <Trans>Adjusted from base {data.commission.basePct.toFixed(0)}%</Trans>
                    </p>
                  )}
                </div>
                {data.commission.effectiveCap > 0 && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs">
                      <Trans>Commission cap</Trans>
                    </p>
                    <p className="text-foreground text-lg font-bold">
                      €{data.commission.effectiveCap.toFixed(2).replace(/\.?0+$/, "")}
                    </p>
                    {data.commission.pppMultiplier !== 1 && data.commission.baseCap > 0 && (
                      <p className="text-muted-foreground text-[0.625rem]">
                        <Trans>From base €{data.commission.baseCap.toFixed(0)}</Trans>
                      </p>
                    )}
                  </div>
                )}
              </div>
              {(data.planTier === "free" || data.planTier === "pro") && (
                <Link
                  to="/account/provider/billing"
                  search={{ tab: "subscription" }}
                  className="text-primary mt-3 inline-flex min-h-11 items-center text-xs font-medium underline-offset-4 hover:underline"
                >
                  <Trans>Upgrade your plan for a lower commission rate.</Trans>
                </Link>
              )}
            </div>
          )}
        </m.div>
      </AnimatePresence>

      {PinGateElement}
    </div>
  );
}

type StripeRequirementKind =
  | "identity"
  | "company_document"
  | "bank"
  | "business_profile"
  | "tos"
  | "address"
  | "dob"
  | "phone"
  | "id_number"
  | "tax_id"
  | "personal"
  | "company"
  | "other";

/**
 * Stripe `requirements.currently_due` keys are dotted API paths
 * (`individual.verification.document`, `external_account`, …). Map the common
 * prefixes to a kind with a plain-words label; anything else falls back to a
 * generic label rather than leaking the raw key.
 */
function stripeRequirementKind(req: string): StripeRequirementKind {
  if (req.startsWith("individual.verification") || req.startsWith("person_")) return "identity";
  if (req.startsWith("company.verification")) return "company_document";
  if (req.startsWith("external_account")) return "bank";
  if (req.startsWith("business_profile")) return "business_profile";
  if (req.startsWith("tos_acceptance")) return "tos";
  if (req.startsWith("individual.address") || req.startsWith("company.address")) return "address";
  if (req.startsWith("individual.dob")) return "dob";
  if (req.startsWith("individual.phone") || req.startsWith("company.phone")) return "phone";
  if (req.startsWith("individual.id_number") || req.startsWith("individual.ssn_last_4")) {
    return "id_number";
  }
  if (req.startsWith("company.tax_id")) return "tax_id";
  if (req.startsWith("individual") || req.startsWith("representative")) return "personal";
  if (req.startsWith("company")) return "company";
  return "other";
}

function StripeRequirementLabel({ kind }: { kind: StripeRequirementKind }) {
  switch (kind) {
    case "identity":
      return <Trans>Identity document</Trans>;
    case "company_document":
      return <Trans>Company verification document</Trans>;
    case "bank":
      return <Trans>Bank account for payouts</Trans>;
    case "business_profile":
      return <Trans>Business details (website or description)</Trans>;
    case "tos":
      return <Trans>Acceptance of Stripe's terms</Trans>;
    case "address":
      return <Trans>Address</Trans>;
    case "dob":
      return <Trans>Date of birth</Trans>;
    case "phone":
      return <Trans>Phone number</Trans>;
    case "id_number":
      return <Trans>Personal identification number</Trans>;
    case "tax_id":
      return <Trans>Company tax ID</Trans>;
    case "personal":
      return <Trans>Personal details</Trans>;
    case "company":
      return <Trans>Company details</Trans>;
    default:
      return <Trans>Other details requested by Stripe</Trans>;
  }
}
